/**
 * Overlays leave even when their closing animation never reports its end.
 *
 * reka-ui — under every Nuxt UI menu, popover, select, tooltip, toast, modal and slideover —
 * removes a closing overlay only when its leave animation sends `animationend` (or
 * `animationcancel`); it has no fallback. Chrome never sends it for an animation that is
 * running when a print starts, and a menu item that prints starts the print a few
 * milliseconds into the menu's close: the menu stayed on screen for good, with the page's
 * pointer events still off, so nothing could close it. The kit's CodSheet ends its own
 * leave on a timer for the same reason; this does it once for every overlay in the app: an
 * element that turns `data-state="closed"` while it animates and hears no end of that
 * animation within LEAVE_FALLBACK_MS is sent one, and reka finishes the leave as usual.
 */

/** Well past the overlays' 100–200 ms leave, so a slow frame never cuts a real one short. */
export const LEAVE_FALLBACK_MS = 1000

export function installOverlayLeaveFallback(root: Document = document): () => void {
    const pending = new Map<Element, () => void>()

    function watchLeave(element: Element): void {
        pending.get(element)?.()

        // reka matches the event against the element's animation-name (the first one).
        const animationName = getComputedStyle(element).animationName.split(',')[0].trim()
        if (!animationName || animationName === 'none') return

        const onEnd = (event: Event) => {
            if (event.target === element) stop()
        }
        const timer = setTimeout(() => {
            stop()
            if (element.isConnected && element.getAttribute('data-state') === 'closed') {
                element.dispatchEvent(new AnimationEvent('animationend', { animationName }))
            }
        }, LEAVE_FALLBACK_MS)

        function stop() {
            clearTimeout(timer)
            element.removeEventListener('animationend', onEnd)
            element.removeEventListener('animationcancel', onEnd)
            pending.delete(element)
        }

        element.addEventListener('animationend', onEnd)
        element.addEventListener('animationcancel', onEnd)
        pending.set(element, stop)
    }

    const observer = new MutationObserver((records) => {
        for (const { target } of records) {
            if (!(target instanceof Element)) continue
            if (target.getAttribute('data-state') === 'closed') watchLeave(target)
            else pending.get(target)?.()
        }
    })
    observer.observe(root.documentElement, { subtree: true, attributes: true, attributeFilter: ['data-state'] })

    return () => {
        observer.disconnect()
        for (const stop of [...pending.values()]) stop()
    }
}
